import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import Button from 'components/ui/button';
import { CicloDoSeletor } from 'components/ui/seletor-de-ciclo';
import { useAuth } from 'context/AuthContext';
import { useCicloDaTela } from 'hooks/useCicloDaTela';
import { getCatalogoExtrato, getLiveloPoints, getPlayerInfo, getResultadosByUser } from 'services/dashboard.service';
import { loadExperiencePurchase } from 'services/experienceCataolog.service';
import { ciclosDeFechamento } from 'utils/ciclos-de-fechamento';
import { FOCO_VISIVEL } from 'utils/foco';

import AvisoDoExtrato from './components/AvisoDoExtrato';
import CabecalhoDaComposicao from './components/CabecalhoDaComposicao';
import CabecalhoDoExtrato from './components/CabecalhoDoExtrato';
import CartaoDoExtrato from './components/CartaoDoExtrato';
import CatalogoLivelo1S2023 from './components/CatalogoLivelo1S2023';
import FaixaDoTotal from './components/FaixaDoTotal';
import HeroDoTotal from './components/HeroDoTotal';
import { EntradaDoExtrato, FaseLegada, montarExtrato, periodoDoCiclo } from './extrato-modelo';

const AVISO_DE_APURACAO =
  'Caro(a) participante, a premiação ainda está em apuração. O extrato pessoal foi disponibilizado de forma prévia e não é garantia de premiação. O crédito da premiação está condicionado a verificação de impedimentos, conforme IN 403-1, item 1.6.';

/** Erro e dados guardam o ciclo a que pertencem: de outro ciclo, valem como "carregando". */
type Carga =
  | { estado: 'carregando' }
  | { estado: 'erro'; cicloId: string }
  | { estado: 'pronto'; cicloId: string; dados: EntradaDoExtrato };

const CARREGANDO: Carga = { estado: 'carregando' };

/** Dados do extrato de um ciclo, com as mesmas leituras da página antiga. */
async function carregarExtrato(jogador: string, ciclo: CicloDoSeletor & { date_start: number }): Promise<EntradaDoExtrato> {
  const [info, livelo, resgate] = await Promise.all([
    getPlayerInfo(jogador, ciclo.title),
    getLiveloPoints(ciclo._id),
    loadExperiencePurchase(jogador, ciclo.title),
  ]);

  // Dado legado próprio: `catalog_extract` (1S2023) e `resultado_cultura_2` (1S2023 e 1S2024).
  const periodo = periodoDoCiclo(ciclo.date_start);
  let fasesLegadas: FaseLegada[] | undefined;
  let comportamentoCultura: string | undefined;
  if (periodo.antesDo2S2023) {
    const [extrato, cultura] = await Promise.all([getCatalogoExtrato(), getResultadosByUser(jogador, ciclo._id)]);
    fasesLegadas = extrato?.[0]?.achiviment;
    comportamentoCultura = cultura?.[0]?.behavior;
  }
  if (periodo.desde1S2024 && !periodo.desde2S2024) {
    comportamentoCultura = (await getResultadosByUser(jogador, ciclo._id))?.[0]?.behavior;
  }

  return {
    ciclo,
    livelo: livelo?.[0],
    tipoDeAcordo: info?.tipo_de_acordo,
    temResgate: Array.isArray(resgate) ? resgate.length > 0 : !!resgate,
    fasesLegadas,
    comportamentoCultura,
  };
}

function Esqueleto() {
  return (
    <div aria-busy="true" className="flex flex-col gap-2.5">
      <p className="sr-only" role="status">
        Carregando o extrato
      </p>
      {[0, 1, 2].map(i => (
        <div key={i} className="h-[88px] animate-pulse rounded-xl bg-surface-200 motion-reduce:animate-none" />
      ))}
    </div>
  );
}

export default function Catalogo() {
  const { user, realCycle } = useAuth();
  const rede = !!user?.extra?.rede;

  // Mesmos ciclos das outras páginas de fechamento (teto conferido por `cycle-ceilings.spec.ts`).
  const opcoes = useMemo(() => ciclosDeFechamento(realCycle) as (CicloDoSeletor & { date_start: number })[], [realCycle]);

  // CIC-10, CIC-11: o ciclo do extrato é o ciclo do app. Fora dos ciclos de fechamento (o 2S2026, aberto), o mais
  // recente deles, sem aviso e sem mudar o ciclo do app (CIC-16, pedido da homologação).
  const { cicloExibido: ciclo, trocar: trocarCiclo } = useCicloDaTela(opcoes);
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    if (!user?._id || !ciclo || rede) return;
    let ativo = true;

    // Troca de ciclo começa do zero: nada do ciclo anterior fica na tela.
    setCarga(CARREGANDO);
    carregarExtrato(user._id, ciclo)
      .then(dados => ativo && setCarga({ estado: 'pronto', cicloId: ciclo._id, dados }))
      .catch(() => ativo && setCarga({ estado: 'erro', cicloId: ciclo._id }));

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, ciclo?._id, rede, tentativa]);

  // No render da troca de ciclo, antes do efeito rodar, a carga ainda é do ciclo anterior.
  const cargaDoCiclo = carga.estado !== 'carregando' && carga.cicloId === ciclo?._id ? carga : CARREGANDO;
  const extrato = montarExtrato(cargaDoCiclo.estado === 'pronto' ? cargaDoCiclo.dados : { ciclo: rede ? undefined : ciclo });

  return (
    <>
      <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
        <CabecalhoDoExtrato titulo={extrato.tituloDaPagina} emApuracao={extrato.emApuracao} />

        {!rede && (
          <>
            {extrato.emApuracao && <AvisoDoExtrato linhas={[AVISO_DE_APURACAO]} />}
            {extrato.catalogoLivelo && <CatalogoLivelo1S2023 />}
            {cargaDoCiclo.estado === 'pronto' && extrato.avisoDoTopo && <AvisoDoExtrato linhas={extrato.avisoDoTopo} />}

            {cargaDoCiclo.estado === 'pronto' && (
              <HeroDoTotal
                tituloDoTotal={extrato.tituloDoTotal}
                rotulo={extrato.rotuloDoTotal}
                total={extrato.total}
                unidade={extrato.unidadeDoTotal}
                cartoes={extrato.cartoes}
              />
            )}

            <section aria-label="Composição do extrato">
              <CabecalhoDaComposicao
                cicloId={ciclo?._id}
                cicloTitulo={ciclo?.title}
                opcoes={opcoes}
                onCiclo={trocarCiclo}
              />

              {cargaDoCiclo.estado === 'carregando' && <Esqueleto />}

              {cargaDoCiclo.estado === 'erro' && (
                <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-brand-white px-6 py-5 shadow-jj-01">
                  <p className="m-0 text-[13px] text-content">Não foi possível carregar o extrato deste ciclo.</p>
                  <Button variant="solido" size="sm" onClick={() => setTentativa(n => n + 1)}>
                    Tentar de novo
                  </Button>
                </div>
              )}

              {cargaDoCiclo.estado === 'pronto' && (
                <div className="flex flex-col gap-2.5">
                  {/* Só as premiações são itens da lista; o total fica fora dela (PREM-24, PREM-26). */}
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                    {extrato.cartoes.map(cartao => (
                      <CartaoDoExtrato key={cartao.id} cartao={cartao} />
                    ))}
                  </ul>
                  <FaixaDoTotal
                    titulo={extrato.tituloDoTotal}
                    rotulo={extrato.rotuloDoTotal}
                    cicloTitulo={ciclo?.title}
                    total={extrato.total}
                    unidade={extrato.unidadeDoTotal}
                    aviso={extrato.avisoDoTotal}
                  />
                </div>
              )}
            </section>

            {cargaDoCiclo.estado === 'pronto' && extrato.emApuracao && (
              // SPEC_DEVIATION: cor `content` (design `text-soft`, 4,43:1 sobre o cinza da página).
              <p tabIndex={0} className={`m-0 mt-4 text-[11px] leading-[1.6] text-content ${FOCO_VISIVEL}`}>
                Valores prévios, sujeitos a alteração até o encerramento da apuração do ciclo.
              </p>
            )}
          </>
        )}
      </div>
      <Outlet />
    </>
  );
}
